import { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import ProjectCard from "./ProjectCard";
import ButtonElement from "@/components/elements/Button/Button";
import { useNavigate } from "react-router-dom";
import TitleWithContent from "@/components/elements/TitleWithContent/TitleWithContent";
import { client } from "@/lib/sanity";

const COMPLETED_SUBCATEGORIES = [
  "Hotel, Hydropower, Parks, Institutional Buildings",
  "Residential Building",
];

const subcategoryMap: { [key: string]: string[] } = {
  "Hotel, Hydropower, Parks, Institutional Buildings": [
    "Hotel",
    "Hydropower",
    "Parks",
    "Institutional Buildings",
  ],
  "Residential Building": ["Residential"],
};

const cardItemVariants = {
  hidden: { opacity: 0, scale: 0.8 },
  visible: {
    opacity: 1,
    scale: 1,
    transition: { duration: 1.2, ease: "easeOut" },
  },
};

const CompletedSubcategoryPage = () => {
  const navigate = useNavigate();
  const [selectedSubcategory, setSelectedSubcategory] = useState<string>(
    COMPLETED_SUBCATEGORIES[0]
  );
  const [projectList, setProjectList] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [animateCards, setAnimateCards] = useState(true);
  const previousCategoryRef = useRef<string | null>(null);

  useEffect(() => {
  const fetchProjects = async () => {
    setLoading(true);
    try {
      // Fetch completed projects from Sanity (with _updatedAt)
      const sanityProjects = await client.fetch(
        `*[_type == "project" && category == "Completed Projects"]{
          _id,
          title,
          category,
          subCategory,
          "imageUrl": image.asset->url,
          slug,
          _updatedAt
        }`
      );

      // Fetch completed projects from local JSON
      const localRes = await fetch(`/projects.json?v=${Date.now()}`);
      const localData = await localRes.json();
      const completedLocal = localData.filter(
        (p: any) => p.category === "Completed Projects"
      );

      // Sort sanity projects by _updatedAt descending (most recent first)
      const sortedSanityProjects = sanityProjects.sort(
        (a, b) =>
          new Date(b._updatedAt).getTime() - new Date(a._updatedAt).getTime()
      );

      // Combine: sanity projects first, then local projects (no sorting on local)
      const combined = [...sortedSanityProjects, ...completedLocal];

      setProjectList(combined);
    } catch (err: any) {
      setError(err.message || "Error fetching projects");
    } finally {
      setLoading(false);
    }
  };
    fetchProjects();
  }, []);

  // Normalize subCategory field to always be an array for filtering
  const normalizeSubCategory = (subCategory: any): string[] => {
    if (!subCategory) return [];
    if (Array.isArray(subCategory)) return subCategory;
    if (typeof subCategory === "string") return [subCategory];
    return [];
  };

  // Filter projects by selected subcategory
  const filteredProjects = projectList.filter((project) => {
    // Normalize subCategory for each project
    const normalizedSub = normalizeSubCategory(project.subCategory);

    // Check category and if any of the project's subcategories match the selected group
    return (
      project.category === "Completed Projects" &&
      subcategoryMap[selectedSubcategory].some((sub) =>
        normalizedSub.includes(sub)
      )
    );
  });

  useEffect(() => {
    const prev = previousCategoryRef.current;
    setAnimateCards(prev !== selectedSubcategory);
    previousCategoryRef.current = selectedSubcategory;
  }, [selectedSubcategory]);

  if (loading)
    return (
      <div className="text-center mt-24 text-lg text-gray-500 ">
        Loading projects...
      </div>
    );

  if (error)
    return (
      <div className="text-center mt-24 text-red-600 ">
        Error loading projects: {error}
      </div>
    );

  return (
    <div className="2xl:px-40 xl:px-35 lg:px-20 md:px-15 px-5 my-[5rem] ">
      <motion.div
        initial={{ opacity: 0, scaleX: 0 }}
        animate={{ opacity: 1, scaleX: 1 }}
        transition={{ duration: 0.5 }}
      >
        <TitleWithContent title="Completed Projects" />
      </motion.div>

      <div className="flex flex-col lg:flex-row gap-10 px-5 md:px-0">
        {/* Sidebar for selecting subcategory */}
        <div className="relative w-full max-w-[320px] mx-auto 2xl:ml-[7rem] xl:ml-[5rem] lg:ml-[4rem] flex flex-col">
          {COMPLETED_SUBCATEGORIES.map((subcat) => {
            const isActive = selectedSubcategory === subcat;
            return (
              <ButtonElement
                key={subcat}
                btnText={subcat}
                onClick={() => setSelectedSubcategory(subcat)}
                className={`w-full rounded-sm !py-3 mb-2 text-left ${
                  isActive
                    ? "bg-custom-secolor font-semibold border-l-4 border-white bg-opacity-70"
                    : "bg-custom-secolor hover:bg-custom-secolor"
                }`}
              />
            );
          })}
        </div>

        {/* Projects Grid */}
        <div className="flex-1">
          <motion.div
            className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6"
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
          >
            {filteredProjects.length > 0 ? (
              filteredProjects.map((project) => (
                <motion.div
                  key={`${project._id || project.id}-${selectedSubcategory}-${animateCards}`}
                  variants={cardItemVariants}
                  initial={animateCards ? "hidden" : false}
                  animate={animateCards ? "visible" : false}
                >
                  <ProjectCard
                    id={project.id || project._id}
                    slug={project.slug?.current || project.slug} // handle slug object or string
                    imageUrl={project.imageUrl}
                    title={project.title}
                    category={project.category}
                  />
                </motion.div>
              ))
            ) : (
              <div className="text-center col-span-full text-gray-500">
                No projects found in this category.
              </div>
            )}
          </motion.div>
        </div>
      </div>

      <button
        className="mt-10 text-gray-500 hover:text-custom-hov hover:underline transition duration-200 2xl:ml-[7rem] xl:ml-[5rem] lg:ml-[4rem]"
        onClick={() => navigate("/projects")}
      >
        ← Back to Project Categories
      </button>
    </div>
  );
};

export default CompletedSubcategoryPage;
